import React, { useState, useEffect } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Badge } from '@voilajsx/uikit/badge';
import { Input } from '@voilajsx/uikit/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@voilajsx/uikit/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@voilajsx/uikit/dialog';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Label } from '@voilajsx/uikit/label';
import { CreditCard, Plus, Edit, Loader2, Shield } from 'lucide-react';
import { AdminSidebar } from '../components/AdminSidebar';
import { Header, SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { route } from '../../../shared/utils';
import { config } from '../../auth/config';

const GOLD = '#FFD93D';
const BONUS_OPTIONS = [0, 5, 10, 15, 20, 25];

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  priceInr: number;
  status: 'active' | 'inactive';
}

const AdminPacksPage: React.FC = () => {
  const { user, token } = useAuth();

  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<CreditPack | null>(null);
  const [formName, setFormName] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formBonus, setFormBonus] = useState(10);
  const [isSaving, setIsSaving] = useState(false);

  const isAdminSystem = user?.role === 'admin' && user?.level === 'system';

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  });

  const fetchPacks = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/payment/admin/packs`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPacks(data.data?.packs || data.packs || data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdminSystem && token) fetchPacks();
  }, [token, isAdminSystem]);

  const computedCredits = Math.round(Number(formPrice || 0) * (1 + formBonus / 100));
  const bonusCredits = computedCredits - Number(formPrice || 0);

  const openCreateDialog = () => {
    setEditingPack(null);
    setFormName('');
    setFormPrice('');
    setFormBonus(10);
    setDialogOpen(true);
  };

  const openEditDialog = (pack: CreditPack) => {
    setEditingPack(pack);
    setFormName(pack.name);
    setFormPrice(String(pack.priceInr));
    // Reverse-compute bonus %
    const pct = pack.priceInr > 0 ? Math.round(((pack.credits - pack.priceInr) / pack.priceInr) * 100) : 0;
    setFormBonus(BONUS_OPTIONS.includes(pct) ? pct : 10);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const price = Number(formPrice);
    if (!formName || price <= 0) return;
    setIsSaving(true);
    setError(null);
    try {
      const isEdit = !!editingPack;
      const url = isEdit
        ? `${config.api.baseUrl}/api/payment/admin/packs/${editingPack!.id}`
        : `${config.api.baseUrl}/api/payment/admin/packs`;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: formName, credits: computedCredits, priceInr: price }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${res.status}`);
      }
      setSuccess(isEdit ? 'Pack updated' : 'Pack created');
      setDialogOpen(false);
      fetchPacks();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStatus = async (pack: CreditPack) => {
    const newStatus = pack.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${config.api.baseUrl}/api/payment/admin/packs/${pack.id}/status`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed');
      setSuccess(`Pack ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchPacks();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getBonusPct = (pack: CreditPack) => {
    return pack.priceInr > 0 ? Math.round(((pack.credits - pack.priceInr) / pack.priceInr) * 100) : 0;
  };

  if (!user) return null;
  if (!isAdminSystem) {
    return (
      <PageLayout size="full">
        <SEO title="Access Denied" />
        <Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6 text-center">
                <Shield className="h-12 w-12 mx-auto mb-4 text-destructive" />
                <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                <p className="text-muted-foreground mb-4">Only system administrators can manage credit packs.</p>
                <Button asChild><a href={route('/admin')}>Back to Admin</a></Button>
              </CardContent>
            </Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  return (
    <PageLayout size="full">
      <SEO title="Credit Packs" description="Manage credit pack offerings" />
      <Header />
      <PageLayout.Content>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <AdminSidebar />
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 72 }}>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold tracking-tight">Credit Packs</h1>
                  <p className="text-muted-foreground">Manage credit pack offerings and pricing</p>
                </div>
                <Button onClick={openCreateDialog} className="gap-2">
                  <Plus className="h-4 w-4" /> Add Pack
                </Button>
              </div>

              {success && (
                <Alert className="bg-green-500/10 border-green-500/30 text-green-400">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}
              {error && (
                <Alert className="bg-red-500/10 border-red-500/30 text-red-400">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" /> Credit Packs ({packs.length})
                  </CardTitle>
                  <CardDescription>Available credit packs for purchase</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="space-y-2 py-2">
                      {[1,2,3,4].map(i => <div key={i} className="h-12 rounded-md bg-zinc-800 animate-pulse" />)}
                    </div>
                  ) : packs.length === 0 ? (
                    <div className="text-center py-12">
                      <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-lg font-medium mb-2">No credit packs</p>
                      <p className="text-muted-foreground">Create one to get started.</p>
                    </div>
                  ) : (
                    <div className="rounded-md border border-zinc-700">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-zinc-700">
                            <TableHead className="border-r border-zinc-700">Name</TableHead>
                            <TableHead className="border-r border-zinc-700 text-right">Amount (₹)</TableHead>
                            <TableHead className="border-r border-zinc-700 text-right">Credits</TableHead>
                            <TableHead className="border-r border-zinc-700 text-center">Bonus</TableHead>
                            <TableHead className="border-r border-zinc-700">Status</TableHead>
                            <TableHead className="w-[150px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {packs.map(pack => {
                            const bonus = getBonusPct(pack);
                            return (
                              <TableRow key={pack.id} className="border-b border-zinc-700">
                                <TableCell className="border-r border-zinc-700 font-medium">{pack.name}</TableCell>
                                <TableCell className="border-r border-zinc-700 text-right font-bold">₹{pack.priceInr}</TableCell>
                                <TableCell className="border-r border-zinc-700 text-right" style={{ color: GOLD, fontWeight: 700 }}>{pack.credits}</TableCell>
                                <TableCell className="border-r border-zinc-700 text-center">
                                  {bonus > 0 ? (
                                    <span style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>+{bonus}%</span>
                                  ) : (
                                    <span style={{ color: '#555', fontSize: 12 }}>0%</span>
                                  )}
                                </TableCell>
                                <TableCell className="border-r border-zinc-700">
                                  <Badge variant={pack.status === 'active' ? 'default' : 'destructive'}>{pack.status}</Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Edit" onClick={() => openEditDialog(pack)}>
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => toggleStatus(pack)} className="text-xs px-2">
                                      {pack.status === 'active' ? 'Deactivate' : 'Activate'}
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </PageLayout.Content>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#fff' }}>{editingPack ? 'Edit Credit Pack' : 'Create Credit Pack'}</DialogTitle>
            <DialogDescription>
              {editingPack ? 'Update the credit pack details.' : 'Define a new credit pack.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Pack Name</Label>
              <Input id="name" value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g., Starter" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">Amount (₹)</Label>
              <Input id="price" type="number" min="1" value={formPrice} onChange={e => setFormPrice(e.target.value)} placeholder="e.g., 500" />
            </div>

            <div className="space-y-2">
              <Label>Bonus Percentage</Label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {BONUS_OPTIONS.map(pct => (
                  <button
                    key={pct}
                    onClick={() => setFormBonus(pct)}
                    style={{
                      padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      border: formBonus === pct ? `2px solid ${GOLD}` : '1px solid #333',
                      background: formBonus === pct ? 'rgba(255,217,61,0.1)' : '#0a0a0a',
                      color: formBonus === pct ? GOLD : '#888',
                    }}
                  >
                    {pct === 0 ? 'Same' : `+${pct}%`}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            {Number(formPrice) > 0 && (
              <div style={{ background: '#111', borderRadius: 10, padding: '14px 16px', marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ color: '#888', fontSize: 13 }}>User pays</span>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>₹{Number(formPrice)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ color: '#888', fontSize: 13 }}>Credits received</span>
                  <span style={{ color: GOLD, fontWeight: 700, fontSize: 15 }}>{computedCredits}</span>
                </div>
                {bonusCredits > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#888', fontSize: 13 }}>Bonus</span>
                    <span style={{ color: '#22c55e', fontWeight: 600, fontSize: 13 }}>+{bonusCredits} credits ({formBonus}%)</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving || !formName || Number(formPrice) <= 0} style={{ background: GOLD, color: '#111' }}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingPack ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminPacksPage;
